import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { User, UserRole } from '../../types';
import { FaUser, FaPhone, FaLock, FaUserShield, FaKey, FaCheckCircle } from 'react-icons/fa';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

interface CreateAdminFormValues {
  name: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  secretKey: string;
}

const CreateAdmin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const initialValues: CreateAdminFormValues = {
    name: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    secretKey: '',
  };

  const validationSchema = Yup.object({
    name: Yup.string()
      .required('الاسم مطلوب')
      .min(3, 'الاسم يجب أن يكون على الأقل 3 أحرف'),
    phoneNumber: Yup.string()
      .required('رقم الهاتف مطلوب')
      .matches(/^[0-9]+$/, 'رقم الهاتف يجب أن يحتوي على أرقام فقط')
      .min(10, 'رقم الهاتف غير صالح')
      .max(15, 'رقم الهاتف غير صالح'),
    password: Yup.string()
      .required('كلمة المرور مطلوبة')
      .min(6, 'كلمة المرور يجب أن تكون على الأقل 6 أحرف'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'كلمات المرور غير متطابقة')
      .required('تأكيد كلمة المرور مطلوب'),
    secretKey: Yup.string()
      .required('كلمة السر السرية مطلوبة')
  });

  const handleSubmit = async (values: CreateAdminFormValues) => {
    try {
      setLoading(true);
      setError(null);

      // التحقق من كلمة السر السرية (يمكنك تغييرها حسب رغبتك)
      if (values.secretKey !== 'admin123456') {
        throw new Error('كلمة السر السرية غير صحيحة');
      }

      // استخدام رقم الهاتف كبريد إلكتروني مؤقت للتسجيل
      const email = `${values.phoneNumber}@example.com`;

      // إنشاء حساب المسؤول
      const userCredential = await createUserWithEmailAndPassword(auth, email, values.password);
      const user = userCredential.user;

      // إنشاء بيانات المسؤول في Firestore
      const adminData: User = {
        id: user.uid,
        name: values.name,
        email,
        phoneNumber: values.phoneNumber,
        role: UserRole.ADMIN,
        isVerified: true,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      await setDoc(doc(db, 'users', user.uid), adminData);
      setSuccess(true);

      // الانتظار لمدة ثانيتين ثم التوجيه إلى صفحة تسجيل دخول المسؤول
      setTimeout(() => {
        navigate('/admin/login');
      }, 2000);

    } catch (err: any) {
      console.error('Error creating admin:', err);
      setError(err.message || 'حدث خطأ أثناء إنشاء حساب المسؤول');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="auth-card">
            <Card.Header className="bg-primary text-white text-center py-4">
              <h2 className="mb-0">إنشاء حساب مسؤول جديد</h2>
            </Card.Header>
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <div className="auth-icon-container">
                  <FaUserShield size={40} className="auth-icon" />
                </div>
                <h4 className="mt-3">إضافة مسؤول جديد</h4>
                <p className="text-muted">أنشئ حساب مسؤول جديد للتحكم في المنصة</p>
              </div>
              
              {error && <Alert variant="danger">{error}</Alert>}
              {success && (
                <Alert variant="success">
                  تم إنشاء حساب المسؤول بنجاح! سيتم توجيهك إلى صفحة تسجيل الدخول...
                </Alert>
              )}
              
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ handleSubmit, isSubmitting }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-4" controlId="name">
                          <Form.Label className="fw-bold">الاسم الكامل</Form.Label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <FaUser className="text-primary" />
                            </span>
                            <Field
                              as={Form.Control}
                              type="text"
                              name="name"
                              placeholder="أدخل اسم المسؤول"
                              className="py-2"
                            />
                          </div>
                          <ErrorMessage
                            name="name"
                            component="div"
                            className="text-danger mt-1"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-4" controlId="phoneNumber">
                          <Form.Label className="fw-bold">رقم الهاتف</Form.Label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <FaPhone className="text-primary" />
                            </span>
                            <Field
                              as={Form.Control}
                              type="text"
                              name="phoneNumber"
                              placeholder="أدخل رقم هاتف المسؤول"
                              className="py-2"
                            />
                          </div>
                          <ErrorMessage
                            name="phoneNumber"
                            component="div"
                            className="text-danger mt-1"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-4" controlId="password">
                          <Form.Label className="fw-bold">كلمة المرور</Form.Label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <FaLock className="text-primary" />
                            </span>
                            <Field
                              as={Form.Control}
                              type="password"
                              name="password"
                              placeholder="أدخل كلمة المرور"
                              className="py-2"
                            />
                          </div>
                          <ErrorMessage
                            name="password"
                            component="div"
                            className="text-danger mt-1"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-4" controlId="confirmPassword">
                          <Form.Label className="fw-bold">تأكيد كلمة المرور</Form.Label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <FaCheckCircle className="text-primary" />
                            </span>
                            <Field
                              as={Form.Control}
                              type="password"
                              name="confirmPassword"
                              placeholder="أعد إدخال كلمة المرور"
                              className="py-2"
                            />
                          </div>
                          <ErrorMessage
                            name="confirmPassword"
                            component="div"
                            className="text-danger mt-1"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-4" controlId="secretKey">
                          <Form.Label className="fw-bold">كلمة السر السرية</Form.Label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <FaKey className="text-primary" />
                            </span>
                            <Field
                              as={Form.Control}
                              type="password"
                              name="secretKey"
                              placeholder="أدخل كلمة السر السرية"
                              className="py-2"
                            />
                          </div>
                          <ErrorMessage
                            name="secretKey"
                            component="div"
                            className="text-danger mt-1"
                          />
                          <Form.Text className="text-muted">
                            هذه الكلمة مطلوبة لإنشاء حساب المسؤول. يرجى التواصل مع المطور للحصول عليها.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-between mt-4">
                      <Button
                        variant="secondary"
                        type="button"
                        className="px-4"
                        onClick={() => navigate('/admin/dashboard')}
                      >
                        إلغاء
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        className="px-4 d-flex align-items-center"
                        disabled={loading || isSubmitting}
                      >
                        {loading ? (
                          <>جاري إنشاء الحساب...</>
                        ) : (
                          <>
                            <FaUserShield className="me-2" />
                            إنشاء حساب المسؤول
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CreateAdmin;
